import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { auth } from '../../../firebase';
import { getApiUrl } from '../../../utils/apiConfig';
import { formatNumber } from '../../../i18n';
import { tokens } from '../../../theme/tokens';
import { useToast } from '../../../contexts/ToastContext';
import {
  ChipInput,
  EmptyState,
  FormDialog,
  InlineMessage,
  SectionCard,
  StatusChip,
  TableSkeleton,
  statusLabel,
} from '../../ui';
import {
  BundleQualifier,
  BundleQualifierKind,
  BundleRule,
  BundleRuleDraft,
  BundleRuleFilter,
  BundleStacking,
  QUALIFIER_KIND_LABELS,
  STACKING_LABELS,
  blankQualifier,
  countByStatus,
  describeQualifier,
  draftFromRule,
  filterRules,
  qualifierProblem,
  updateBody,
} from './bundleRuleModel';

const QUERY_KEY = ['admin-bundle-rules'];

async function apiCall<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const response = await fetch(`${getApiUrl()}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) {
    throw new Error(body?.error?.message || body?.error || body?.message || `Request failed (${response.status})`);
  }
  return body.data as T;
}

const money = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const windowLabel = (rule: BundleRule) =>
  rule.startAt || rule.endAt ? `${day(rule.startAt) || 'now'} → ${day(rule.endAt) || 'open'}` : 'No dates';

const FILTERS: Array<{ key: BundleRuleFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'needs_review', label: 'Needs review' },
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
];

const EMPTY_COPY: Record<BundleRuleFilter, string> = {
  all: 'No bundle rules yet. Rules appear when a sheet with sold-with offers is approved while bundle rebates are on.',
  needs_review: 'Every rule has a qualifier. Nothing waits for review.',
  active: 'No rule is active.',
  disabled: 'No rule is disabled.',
};

/**
 * Bundle ("sold with") rebate rules, on the admin Rebate programs page (MOT-387).
 * One table: status filter with counts, search, and an edit dialog for the
 * name, stacking, status and qualifier. `?rule=<id>` opens one rule, which is
 * how the product detail panel's "Open bundle rule" link lands here.
 */
export const AdminBundleRulesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<BundleRuleFilter>('all');
  const [search, setSearch] = useState('');

  const rulesQuery = useQuery<BundleRule[]>({
    queryKey: QUERY_KEY,
    queryFn: () => apiCall<BundleRule[]>('/admin/bundle-rebates'),
  });
  const rules = useMemo(() => rulesQuery.data ?? [], [rulesQuery.data]);
  const counts = useMemo(() => countByStatus(rules), [rules]);
  const visible = useMemo(() => filterRules(rules, filter, search), [rules, filter, search]);

  const openId = searchParams.get('rule');
  const openRule = openId ? rules.find((r) => r.id === openId) ?? null : null;
  const setOpenId = (id: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('rule', id);
        else next.delete('rule');
        return next;
      },
      { replace: true },
    );
  };

  const saveMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      apiCall<BundleRule>(`/admin/bundle-rebates/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: (rule) => {
      queryClient.setQueryData<BundleRule[]>(QUERY_KEY, (prev) => (prev || []).map((r) => (r.id === rule.id ? { ...r, ...rule } : r)));
      void queryClient.invalidateQueries({ queryKey: ['admin-rebate-overview'] });
      showToast(`Saved "${rule.name}" · ${statusLabel(rule.status)}`, 'success');
      setOpenId(null);
    },
    onError: (error: Error) => showToast(error.message || 'Could not save the rule', 'error'),
  });

  const coverage = `${formatNumber(counts.all)} rule${counts.all === 1 ? '' : 's'} · ${formatNumber(counts.active)} active · ${formatNumber(counts.needs_review)} need review · ${formatNumber(counts.disabled)} disabled`;

  return (
    <SectionCard
      dense
      title="Bundle rebate rules"
      subtitle={`Sold-with offers from vendor sheets, as rules: the item that earns the rebate and what must be in the cart with it. ${rulesQuery.isSuccess ? coverage : ''}`}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', px: 2, py: 1.5 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next: BundleRuleFilter | null) => next && setFilter(next)}
          aria-label="Filter rules by status"
          sx={{ flexWrap: 'wrap' }}
        >
          {FILTERS.map((f) => (
            <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none' }}>
              {f.label}
              <Box
                component="span"
                sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums', color: f.key === 'needs_review' && counts.needs_review > 0 ? 'warning.main' : 'text.secondary' }}
              >
                {formatNumber(counts[f.key])}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search name, brand, sheet text or product id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: '1 1 240px', minWidth: 0 }}
        />
      </Box>

      {rulesQuery.isLoading && <Box sx={{ px: 2, pb: 2 }}><TableSkeleton rows={5} /></Box>}
      {rulesQuery.isError && (
        <Box sx={{ px: 2 }}>
          <InlineMessage severity="error">
            Could not load the bundle rules. <Button size="small" onClick={() => rulesQuery.refetch()}>Retry</Button>
          </InlineMessage>
        </Box>
      )}
      {rulesQuery.isSuccess && visible.length === 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <EmptyState
            title={search.trim() ? `No rule matches "${search.trim()}".` : EMPTY_COPY[filter]}
          />
        </Box>
      )}
      {rulesQuery.isSuccess && visible.length > 0 && (
        <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
          {visible.map((rule) => (
            <Box
              key={rule.id}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, minHeight: 48, borderTop: 1, borderColor: 'divider' }}
            >
              <Box sx={{ flex: '2 1 280px', minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{rule.name || rule.id}</Typography>
                  <StatusChip label={statusLabel(rule.status)} tone={rule.status === 'needs_review' ? 'caution' : rule.status === 'active' ? 'positive' : 'neutral'} />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {rule.brand} · {rule.organizationId ? `Store ${rule.organizationId}` : 'Network-wide'} · {windowLabel(rule)}
                </Typography>
                <Typography variant="caption" color={rule.qualifier ? 'text.primary' : 'warning.main'} component="div">
                  {describeQualifier(rule.qualifier)}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flex: '0 0 auto' }}>
                −{money(rule.instantRebate)}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setOpenId(rule.id)}>
                {rule.status === 'needs_review' ? 'Review' : 'Edit'}
              </Button>
            </Box>
          ))}
        </Box>
      )}
      {openId && rulesQuery.isSuccess && !openRule && (
        <Box sx={{ px: 2, pb: 2 }}>
          <InlineMessage severity="warning">
            The rule in the link ({openId}) no longer exists. <Button size="small" onClick={() => setOpenId(null)}>Dismiss</Button>
          </InlineMessage>
        </Box>
      )}

      {openRule && (
        <BundleRuleDialog
          key={openRule.id}
          rule={openRule}
          saving={saveMutation.isPending}
          onClose={() => setOpenId(null)}
          onSave={(body) => saveMutation.mutate({ id: openRule.id, body })}
        />
      )}
    </SectionCard>
  );
};

interface DialogProps {
  rule: BundleRule;
  saving: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}

const updatedMs = (r: BundleRule): number => (r.updatedAt ? Date.parse(r.updatedAt) || 0 : 0);

const BundleRuleDialog: React.FC<DialogProps> = ({ rule: live, saving, onClose, onSave }) => {
  // The rule as it was when the dialog opened: the draft is diffed against this,
  // never against a refetched copy, so a save cannot revert someone else's change.
  const [rule, setBase] = useState<BundleRule>(live);
  const [draft, setDraft] = useState<BundleRuleDraft>(() => draftFromRule(live));
  const [anyOfText, setAnyOfText] = useState(() =>
    rule.qualifier?.kind === 'any_of' ? JSON.stringify(rule.qualifier.qualifiers, null, 2) : '[]',
  );
  const [anyOfError, setAnyOfError] = useState<string | null>(null);
  const changedElsewhere = updatedMs(live) > updatedMs(rule);
  const reload = () => {
    setBase(live);
    setDraft(draftFromRule(live));
    setAnyOfText(live.qualifier?.kind === 'any_of' ? JSON.stringify(live.qualifier.qualifiers, null, 2) : '[]');
    setAnyOfError(null);
  };

  const kind = draft.qualifier?.kind ?? '';
  const setQualifier = (qualifier: BundleQualifier | null) => setDraft((d) => ({ ...d, qualifier }));
  const patchQualifier = (patch: Record<string, unknown>) =>
    setDraft((d) => (d.qualifier ? { ...d, qualifier: { ...d.qualifier, ...patch } as BundleQualifier } : d));

  const problem = anyOfError || qualifierProblem(draft.qualifier);
  const activatingWithout = draft.status === 'active' && (!draft.qualifier || rule.offerProductIds.length === 0);
  const body = updateBody(rule, draft);
  const autoActivates = rule.status === 'needs_review' && draft.status === 'needs_review' && !!body?.qualifier && rule.offerProductIds.length > 0;

  return (
    <FormDialog
      open
      title={rule.status === 'needs_review' ? `Review bundle rule · ${rule.name}` : `Edit bundle rule · ${rule.name}`}
      onClose={onClose}
      onSubmit={() => body && onSave(body)}
      submitLabel={autoActivates ? 'Save and activate' : 'Save rule'}
      submitDisabled={!body || !!problem || activatingWithout || changedElsewhere}
      loading={saving}
      maxWidth="md"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {changedElsewhere && (
          <InlineMessage severity="warning">
            This rule changed while you had it open. Reload it to see the change; your edits here are discarded.{' '}
            <Button size="small" onClick={reload}>Reload rule</Button>
          </InlineMessage>
        )}
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
          <Typography variant="overline" color="text.secondary">What the sheet said</Typography>
          <Typography variant="body2">{rule.rawCondition || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {money(rule.instantRebate)} off · {rule.brand} · {windowLabel(rule)} · offer item {rule.rawOfferProduct || rule.offerProductIds.join(', ') || 'not matched'}
          </Typography>
          {rule.reviewNotes && (
            <Typography variant="caption" color="warning.main" component="div">{rule.reviewNotes}</Typography>
          )}
        </Box>

        <TextField
          label="Name"
          size="small"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          inputProps={{ maxLength: 200 }}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            select
            label="Status"
            size="small"
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as BundleRuleDraft['status'] }))}
            sx={{ flex: '1 1 220px' }}
            helperText={
              activatingWithout
                ? 'A rule needs a qualifier and an offer product before it can be active.'
                : rule.status === 'needs_review'
                  ? 'Saving a qualifier on a rule awaiting review activates it.'
                  : 'Only active rules count at checkout and in the rebate report.'
            }
          >
            {rule.status === 'needs_review' && <MenuItem value="needs_review">Needs review</MenuItem>}
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </TextField>
          <TextField
            select
            label="Stacking"
            size="small"
            value={draft.stacking}
            onChange={(e) => setDraft((d) => ({ ...d, stacking: e.target.value as BundleStacking }))}
            sx={{ flex: '1 1 260px' }}
            helperText="How this rule combines with other rebates on the offer item."
          >
            {(Object.keys(STACKING_LABELS) as BundleStacking[]).map((s) => (
              <MenuItem key={s} value={s}>{STACKING_LABELS[s]}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">Qualifier: what must be in the cart</Typography>
          <TextField
            select
            label="Kind"
            size="small"
            fullWidth
            value={kind}
            onChange={(e) => {
              const next = e.target.value as BundleQualifierKind;
              setAnyOfError(null);
              setQualifier(next === rule.qualifier?.kind ? rule.qualifier : blankQualifier(next, rule.brand));
              if (next === 'any_of') setAnyOfText(rule.qualifier?.kind === 'any_of' ? JSON.stringify(rule.qualifier.qualifiers, null, 2) : '[]');
            }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            helperText={draft.qualifier ? describeQualifier(draft.qualifier) : 'Without a qualifier the rule never applies.'}
            sx={{ mt: 1 }}
          >
            <MenuItem value="" disabled>Not set</MenuItem>
            {(Object.keys(QUALIFIER_KIND_LABELS) as BundleQualifierKind[]).map((k) => (
              <MenuItem key={k} value={k}>{QUALIFIER_KIND_LABELS[k]}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            {draft.qualifier?.kind === 'product' && (
              <Box sx={{ flex: '1 1 100%' }}>
                <ChipInput
                  value={draft.qualifier.productIds}
                  onChange={(productIds) => patchQualifier({ productIds })}
                  placeholder="Catalog product id, then Enter"
                  aria-label="Qualifying product ids"
                />
              </Box>
            )}
            {(draft.qualifier?.kind === 'brand_family' || draft.qualifier?.kind === 'category') && (
              <TextField
                label="Brand"
                size="small"
                value={draft.qualifier.brand}
                onChange={(e) => patchQualifier({ brand: e.target.value })}
                sx={{ flex: '1 1 200px' }}
              />
            )}
            {draft.qualifier?.kind === 'brand_family' && (
              <TextField
                label="Family"
                size="small"
                placeholder="e.g. Z Series"
                value={draft.qualifier.family}
                onChange={(e) => patchQualifier({ family: e.target.value })}
                sx={{ flex: '1 1 200px' }}
              />
            )}
            {draft.qualifier?.kind === 'category' && (
              <TextField
                label="Category"
                size="small"
                placeholder="e.g. flashes"
                value={draft.qualifier.category}
                onChange={(e) => patchQualifier({ category: e.target.value })}
                sx={{ flex: '1 1 200px' }}
              />
            )}
            {draft.qualifier?.kind === 'mount' && (
              <>
                <TextField
                  label="Mount"
                  size="small"
                  placeholder="e.g. Sony E"
                  value={draft.qualifier.mount}
                  onChange={(e) => patchQualifier({ mount: e.target.value })}
                  sx={{ flex: '1 1 200px' }}
                />
                <TextField
                  select
                  label="Product"
                  size="small"
                  value={draft.qualifier.productType}
                  onChange={(e) => patchQualifier({ productType: e.target.value })}
                  sx={{ flex: '1 1 160px' }}
                >
                  <MenuItem value="lens">Lens</MenuItem>
                  <MenuItem value="camera">Camera</MenuItem>
                </TextField>
              </>
            )}
            {draft.qualifier?.kind === 'compatible_with' && (
              <>
                <TextField
                  label="Product id"
                  size="small"
                  value={draft.qualifier.productId}
                  onChange={(e) => patchQualifier({ productId: e.target.value })}
                  sx={{ flex: '1 1 220px' }}
                />
                <TextField
                  select
                  label="Linked as"
                  size="small"
                  value={draft.qualifier.association}
                  onChange={(e) => patchQualifier({ association: e.target.value })}
                  sx={{ flex: '1 1 180px' }}
                >
                  <MenuItem value="compatible_lens">Compatible lens</MenuItem>
                  <MenuItem value="compatible_body">Compatible body</MenuItem>
                  <MenuItem value="compatible_accessory">Compatible accessory</MenuItem>
                </TextField>
              </>
            )}
            {draft.qualifier?.kind === 'any_of' && (
              <TextField
                label="Qualifiers (JSON list)"
                size="small"
                multiline
                minRows={5}
                fullWidth
                value={anyOfText}
                error={!!anyOfError}
                helperText={anyOfError || 'A list of qualifiers of the kinds above, e.g. [{"kind":"mount","mount":"Sony E","productType":"lens"}]. Checked again when saved.'}
                onChange={(e) => {
                  const text = e.target.value;
                  setAnyOfText(text);
                  try {
                    const parsed = JSON.parse(text);
                    if (!Array.isArray(parsed)) throw new Error('not a list');
                    setAnyOfError(null);
                    setQualifier({ kind: 'any_of', qualifiers: parsed as BundleQualifier[] });
                  } catch {
                    setAnyOfError('Not a valid JSON list.');
                  }
                }}
                InputProps={{ sx: { fontFamily: tokens.font.mono, fontSize: 12.5 } }}
              />
            )}
          </Box>
          {problem && draft.qualifier && (
            <Typography variant="caption" color="error" component="div" sx={{ mt: 1 }}>{problem}</Typography>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary">
          A qualifier you save is kept when the sheet is derived again. Changes reach checkout discounts and the rebate report on their next run.
        </Typography>
      </Stack>
    </FormDialog>
  );
};

export default AdminBundleRulesTab;
